import React from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ShieldAlert, Monitor, LogOut } from "lucide-react";

export default function DevicePendingPage() {
  const { user, logout, deviceStatus } = useAuth();

  const isBlocked = deviceStatus === "blocked";

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="device-pending-page">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center">
            {isBlocked ? (
              <ShieldAlert className="w-8 h-8 text-red-500" />
            ) : (
              <Monitor className="w-8 h-8 text-amber-500" />
            )}
          </div>
          <h1 className="font-heading text-3xl tracking-tight">
            {isBlocked ? "Device Blocked" : "Device Not Registered"}
          </h1>
        </div>

        <Card className="border border-border shadow-none rounded-sm">
          <CardHeader className="text-center pb-3">
            <CardTitle className="text-lg">
              {isBlocked ? "Access Denied" : "Pending Approval"}
            </CardTitle>
            <CardDescription>
              {isBlocked
                ? "This device has been blocked by your administrator. Please contact your Super Admin to unblock it."
                : "This device is not registered for your account. Please contact your Super Admin to get a device registration link."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-sm bg-muted/50 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Logged in as</span>
                <span className="font-medium">{user?.name || user?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Role</span>
                <span className="font-medium capitalize">{user?.role?.replace("_", " ")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Device Status</span>
                <span className={`font-medium ${isBlocked ? "text-red-500" : "text-amber-500"}`}>
                  {isBlocked ? "Blocked" : "Unregistered"}
                </span>
              </div>
            </div>

            <div className="text-xs text-muted-foreground text-center space-y-1">
              <p>Ask your Super Admin to:</p>
              <ol className="list-decimal list-inside text-left space-y-1 pl-2">
                {isBlocked ? (
                  <li>Go to Security &gt; Device Management and unblock this device</li>
                ) : (
                  <>
                    <li>Go to Security &gt; Device Management</li>
                    <li>Generate a registration link for your account</li>
                    <li>Open the link on this device to register it</li>
                  </>
                )}
              </ol>
            </div>

            <Button
              variant="outline"
              className="w-full rounded-sm"
              onClick={logout}
              data-testid="device-pending-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
